import { type AnalyticsEvents } from 'lib/analytics/events';

import { type GetStartedProgress } from './get-started-progress';

export type GetStartedStepId =
  AnalyticsEvents['get_started_step_clicked']['step'];

/** Colour role shared by a step's call to action and its visual. */
export type GetStartedAccent = 'brand' | 'green' | 'purple';

export const GET_STARTED_STEPS = [
  {
    id: 'source',
    signal: 'has_sources',
    title: 'Set up your knowledge base',
    description:
      'Connect your data sources.\nKapa indexes them and keeps them up to date.',
    actionLabel: 'Connect source',
    accent: 'brand',
  },
  {
    id: 'query',
    signal: 'has_queried',
    title: 'Try your first query',
    description:
      'Ask a question and see an answer grounded in your knowledge, with links to the sources.',
    actionLabel: 'Go to playground',
    accent: 'green',
  },
  {
    id: 'integration',
    signal: 'has_deployed',
    title: 'Deploy an integration',
    description:
      'Connect your own agent through API or MCP, or bring a prebuilt assistant to your users.',
    actionLabel: 'Choose an integration',
    accent: 'purple',
  },
  {
    id: 'analytics',
    signal: 'has_explored_analytics',
    title: 'Explore conversations',
    description:
      'See what people and agents ask. Find gaps in your knowledge and make every answer better.',
    actionLabel: 'Go to conversations',
    accent: 'brand',
  },
] as const satisfies ReadonlyArray<{
  id: GetStartedStepId;
  signal: keyof GetStartedProgress;
  title: string;
  description: string;
  actionLabel: string;
  accent: GetStartedAccent;
}>;

export interface GetStartedStep {
  id: GetStartedStepId;
  title: string;
  description: string;
  actionLabel: string;
  isDone: boolean;
  /** Null until a project is selected, so there is nowhere to send the user yet. */
  href: string | null;
  accent: GetStartedAccent;
}

export const countCompletedSteps = (progress?: GetStartedProgress): number =>
  GET_STARTED_STEPS.filter((step) => progress?.[step.signal]).length;
